import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  addTicketMessage,
  createTicket,
  getTicket,
  listTickets,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatDateTime } from "../utils/formatting"

export function SupportPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState("")
  const [reply, setReply] = useState("")
  const [ticketType, setTicketType] = useState<"PAYMENT_ISSUE" | "ACCOUNT_ISSUE" | "DISPUTE" | "GENERAL_ENQUIRY">(
    (searchParams.get("ticketType") as any) ?? "PAYMENT_ISSUE",
  )
  const [subject, setSubject] = useState("")
  const [description, setDescription] = useState("")
  const [loanId, setLoanId] = useState(searchParams.get("loanId") ?? "")

  const selectedTicketId = searchParams.get("ticketId")

  const ticketsQuery = useQuery({
    queryKey: ["portal-tickets", search],
    queryFn: () => listTickets({ page: 1, search }).then((response) => response),
  })

  const ticketDetailQuery = useQuery({
    queryKey: ["portal-ticket", selectedTicketId],
    queryFn: () => getTicket(selectedTicketId!).then((response) => response.data),
    enabled: Boolean(selectedTicketId),
  })

  const selectedTicket = ticketDetailQuery.data

  const createMutation = useMutation({
    mutationFn: () => createTicket({ ticketType, subject, description, loanId: loanId || undefined }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["portal-tickets"] })
      setSearchParams({ ticketId: response.data.ticketId })
      setSubject("")
      setDescription("")
    },
  })

  const replyMutation = useMutation({
    mutationFn: () => addTicketMessage(selectedTicketId!, reply),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal-ticket", selectedTicketId] })
      queryClient.invalidateQueries({ queryKey: ["portal-tickets"] })
      setReply("")
    },
  })

  const tickets = useMemo(() => ticketsQuery.data?.data ?? [], [ticketsQuery.data])

  useEffect(() => {
    if (!selectedTicketId && tickets[0]?.ticketId) {
      setSearchParams({ ticketId: tickets[0].ticketId })
    }
  }, [selectedTicketId, setSearchParams, tickets])

  return (
    <div className="page-stack support-grid">
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Support</p>
            <h3>Raise a ticket</h3>
          </div>
        </div>
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault()
            createMutation.mutate()
          }}
        >
          <label>
            Ticket type
            <select onChange={(event) => setTicketType(event.target.value as any)} value={ticketType}>
              <option value="PAYMENT_ISSUE">Payment issue</option>
              <option value="ACCOUNT_ISSUE">Account issue</option>
              <option value="DISPUTE">Dispute</option>
              <option value="GENERAL_ENQUIRY">General enquiry</option>
            </select>
          </label>
          <label>
            Loan ID (optional)
            <input onChange={(event) => setLoanId(event.target.value)} value={loanId} />
          </label>
          <label>
            Subject
            <input onChange={(event) => setSubject(event.target.value)} value={subject} />
          </label>
          <label>
            Description
            <textarea onChange={(event) => setDescription(event.target.value)} rows={5} value={description} />
          </label>
          <button className="primary-button" disabled={createMutation.isPending} type="submit">
            {createMutation.isPending ? "Submitting..." : "Create ticket"}
          </button>
        </form>
      </section>

      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Your tickets</p>
            <h3>Conversation history</h3>
          </div>
        </div>
        <label className="search-field">
          Search
          <input onChange={(event) => setSearch(event.target.value)} value={search} />
        </label>
        <div className="support-list">
          {ticketsQuery.isLoading ? (
            <Skeleton height={200} />
          ) : tickets.length === 0 ? (
            <p className="empty-copy">No tickets found.</p>
          ) : (
            tickets.map((ticket: any) => (
              <button
                className={`ticket-preview ${selectedTicketId === ticket.ticketId ? "active" : ""}`}
                key={ticket.ticketId}
                onClick={() => setSearchParams({ ticketId: ticket.ticketId })}
                type="button"
              >
                <strong>{ticket.subject}</strong>
                <span>{ticket.status}</span>
              </button>
            ))
          )}
        </div>

        <div className="support-thread">
          {ticketDetailQuery.isLoading ? (
            <Skeleton height={220} />
          ) : !selectedTicket ? (
            <p className="empty-copy">Choose a ticket to view the thread.</p>
          ) : (
            <>
              <div className="thread-header">
                <h4>{selectedTicket.subject}</h4>
                <span className="status-pill neutral">{selectedTicket.status}</span>
              </div>
              <p className="subtle">{selectedTicket.description}</p>
              <div className="thread-messages">
                {selectedTicket.messages.map((message: any) => (
                  <div className="message-bubble" key={message.messageId}>
                    <strong>{message.authorName}</strong>
                    <p>{message.body}</p>
                    <span className="subtle">{formatDateTime(message.createdAt)}</span>
                  </div>
                ))}
              </div>
              <form
                className="form-grid"
                onSubmit={(event) => {
                  event.preventDefault()
                  replyMutation.mutate()
                }}
              >
                <label>
                  Reply
                  <textarea onChange={(event) => setReply(event.target.value)} rows={4} value={reply} />
                </label>
                <button className="primary-button" disabled={replyMutation.isPending} type="submit">
                  {replyMutation.isPending ? "Sending..." : "Send reply"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
